import React, { useContext, useState, useEffect } from 'react';
import ICryptoWallets, { TCryptoWallets } from './ICryptoWallets';
import InfoBox from '@appComponents/InfoBox/InfoBox';
import WalletForm from '../WalletForm/WalletForm';
import StepButtons from '@appComponents/StepButtons/StepButtons';
import FundsService from '@services/Funds.service';
import ConfirmForm from '@appComponents/ConfirmForm/ConfirmForm';
import ActionBox from '@appComponents/ActionBox/ActionBox';
import { connect } from 'react-redux';
import { TReducers } from '@features/reducers';
import { useCryptoWallets } from './useCryptoWallets';
import { IPrivateWallets } from '../WithdrawAmount/IWithdrawAmount';
import { FundsDispatchContext, FundsAppContext } from '@features/context';
import { deleteCryptoWallet, updateCryptoWallets } from '@funds/features/action.funds';
import { catchError, isFiatCurrency, animateScrollTo, checkAddress } from '@features/utils/functions';
import { MAIN_CURRENCY } from '@bundles/consts';
import { IWithdrawFees } from '../WithdrawForm/IWithdrawForm';
import { addressRegex } from '@funds/features/helpers/validation';

import * as style from './CryptoWalletsStyle.module.scss'
import * as wAmountStyles from '../WithdrawAmount/WithdrawAmountStyle.module.scss';

export const CryptoWallets: React.FunctionComponent<ICryptoWallets> = (props: ICryptoWallets) => {
  const { investorId } = useContext(FundsAppContext);
  const { values, errors, dispatch } = useCryptoWallets();
  const [walletIdToRemove, setWalletIdToRemove] = useState(0);
  const [disabledButton, setDisabledButton] = useState(false);
  const [fees, setFees] = useState<IWithdrawFees[]>([]);

  useEffect(() => {
    FundsService.getWithdrawFees()
      .then((res) => {
        setFees(res.data);
      })
      .catch(err => catchError(err, true));
  }, []);

  const editWalletClick = (el: IPrivateWallets) => {
    const currency = props.availableWallets
      .find(elem => el.supported_withdraw_currency === elem.value);

    dispatch({
      type: 'editMode',
      payload: {
        editMode: true,
        id: el.id,
        currency: currency,
        bfusd_wallet_currency: {
          value: el.currency,
          label: 'Tether (USDT)',
        },
        address: el.address,
        usd_wallet_type: setUsdWalletType(el.network, currency),
        name: el.name,
      },
    });
    animateScrollTo(undefined, document.getElementById('cryptopwallets'), 20);
  };

  const addWalletClick = () => {
    dispatch({
      type: 'setInitial',
      payload: { editMode: true },
    });
    animateScrollTo(undefined, document.getElementById('cryptopwallets'), 20);
  };

  const prevStep = () => {
    dispatch({ type: 'setInitial' });
    animateScrollTo(undefined, document.getElementById('cryptopwallets'), 20);
  };

  const getCurrencyForAPI = () =>
    values.currency && values.currency.value === MAIN_CURRENCY && values.bfusd_wallet_currency
      ? values.bfusd_wallet_currency.value
      : values.currency!.value;

  const saveEditedWallet = () => {
    if (investorId && values.id) {
      setDisabledButton(true);

      const data = {
        type: 'withdraw',
        currency: getCurrencyForAPI(),
        address: values.address!,
        network: values.currency && values.currency.value === MAIN_CURRENCY && values.usd_wallet_type
          ? values.usd_wallet_type.value
          : undefined,
        name: values.name,
      };

      FundsService.updateCryptoWallet(investorId, data, values.id)
        .then((res) => {
          props.updateCryptoWallets(res.data, values.id);
          setDisabledButton(false);
          prevStep();
        })
        .catch((err) => {
          const errors = Object.keys(err.response.data.errors)
            .reduce((prev, curr) => ({ ...prev, [curr]: err.response.data.errors[curr][0] }), {});
          if (Object.keys(errors).length) {
            dispatch({ type: 'setErrors', errors: errors });
          } else {
            catchError(err);
          }
          window.scrollTo(0, 0);
          setDisabledButton(false);
        });
    }
  };

  const saveNewWallet = () => {
    if (investorId) {
      setDisabledButton(true);

      const data = {
        type: 'withdraw',
        currency: getCurrencyForAPI(),
        address: values.address!,
        network: values.currency && values.currency.value === MAIN_CURRENCY && values.usd_wallet_type
          ? values.usd_wallet_type.value
          : undefined,
        name: values.name,
      };

      FundsService.addCryptoWallet(investorId, data)
        .then((res) => {
          props.updateCryptoWallets(res.data);
          setDisabledButton(false);
          prevStep();
        })
        .catch((err) => {
          const errors = Object.keys(err.response.data.errors)
            .reduce((prev, curr) => ({ ...prev, [curr]: err.response.data.errors[curr][0] }), {});
          if (Object.keys(errors).length) {
            dispatch({ type: 'setErrors', errors: errors });
          } else {
            catchError(err);
          }
          window.scrollTo(0, 0);
          setDisabledButton(false);
        });
    }
  };

  const setUsdWalletType = (network: IPrivateWallets['network'], currency: TCryptoWallets['currency']) => (
    network && currency && currency.value
      ? { value: network, label: 'Ethereum' }
      : undefined
  );

  const deleteWallet = (walletId: number) => {
    if (investorId) {
      FundsService.deleteCryptoWallet(investorId, walletId)
        .then(() => {
          setWalletIdToRemove(0);
          props.deleteCryptoWallet(walletId);
        })
        .catch((err) => {
          setWalletIdToRemove(0);
          catchError(err);
          window.scrollTo(0, 0);
        });
    }
  };

  const renderCryptoWallets = () => {
    const wallets = props.cryptoWallets.map(el => (
      <ActionBox
        key={`wallet${el.id}`}
        headline={el.name ? <p>{el.name}</p> : renderCurrency(el.currency)}
        text={<p>{el.address}</p>}
        type={el.type === 'mobile' ? 'empty' : 'edit_cancel'}
        className={style.boxMobile}
        editClick={() => editWalletClick(el)}
        cancelClick={() => setWalletIdToRemove(el.id)}
      />
    ));
    return (
      <>
        <h2 className={style.headline}>Your crypto wallets</h2>
        <div>
          {wallets.length > 0 ? wallets : <InfoBox><p>You don't have any wallets yet.</p></InfoBox>}
          {renderAddBtn('Add New Wallet')}
        </div>
      </>
    );
  };

  const currency = values.currency ? values.currency.value : '';

  const walletAddressWarning = values.address
    ? checkAddress(
      values.address,
      currency,
      values.usd_wallet_type && currency === MAIN_CURRENCY
        ? values.usd_wallet_type.value
        : undefined
    )
    : undefined;

  const renderForm = () => (
    <>
      <FundsDispatchContext.Provider value={dispatch}>
        <WalletForm
          currency={currency}
          values={values}
          errors={errors}
          currencies={props.availableWallets.filter(wallet => wallet.value !== 'staked_eth')}
          addressRegex={addressRegex(fees, values?.currency?.value)}
        />
      </FundsDispatchContext.Provider>
      <StepButtons
        nextStep={values.id ? saveEditedWallet : saveNewWallet}
        prevStep={prevStep}
        isFirstStep={false}
        nextStepEnable={Object.keys(errors).length === 0 && !disabledButton}
        nextText={values.id ? 'Save' : 'Add'}
      />
    </>
  );

  const renderAddBtn = (text: string) => (
    <p onClick={addWalletClick} className={wAmountStyles.addNew}><span>+</span>{text}</p>
  );

  const renderCurrency = (currency: string) => <p>{currency}</p>;

  return (
    <div id="cryptopwallets">
      {values.editMode ? renderForm() : renderCryptoWallets()}
      <ConfirmForm
        closeLightbox={() => setWalletIdToRemove(0)}
        actionFunction={() => deleteWallet(walletIdToRemove)}
        values={{
          headline: 'Are you sure you wish to delete this wallet?',
          paragraph: `Please note that this operation is irreversible.`,
          actionButton: 'Confirm',
        }}
        isVisible={!!walletIdToRemove}
      />
    </div>
  );
};

const mapStateToProps = (store: TReducers) => ({
  availableWallets: (store.fundsReducer.supportedUserWallets || [])
    .filter(el => (el.currency === MAIN_CURRENCY || !isFiatCurrency(el.currency)) && !el.public_equity_shares)
    .map(el => ({ value: el.currency, label: el.currency_name })),
  cryptoWallets: store.fundsReducer.cryptoWallets || [],
});

const mapDispatchToProps = (dispatch: Function) => ({
  deleteCryptoWallet: (walletId: number) => (
    dispatch(deleteCryptoWallet(walletId))
  ),
  updateCryptoWallets: (data: IPrivateWallets, walletIdToRemove?: number) => (
    dispatch(updateCryptoWallets(data, walletIdToRemove))
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(CryptoWallets);
